import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { PartyPopper, ScanLine, UserPlus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODES = (noun) => [
  { id: "admin_only", label: "Admins only", hint: `Only admin accounts see ${noun}.` },
  { id: "selected_users", label: "Selected users", hint: "Admins + the users you pick below." },
  { id: "all_users", label: "All users", hint: `Every registered user sees ${noun}.` },
  { id: "disabled", label: "Disabled", hint: "Hidden from everyone, including admins." },
];

// Generic admin panel for a server-resolved feature-access config
// (db.feature_access). One instance per gated feature.
const AccessPanel = ({ feature, title, question, noun, icon: Icon, testPrefix }) => {
  const [cfg, setCfg] = useState(null);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [pickId, setPickId] = useState("");

  const load = useCallback(async () => {
    try {
      const [a, u] = await Promise.all([
        axios.get(`${API}/admin/feature-access/${feature}`),
        axios.get(`${API}/admin/users`),
      ]);
      setCfg(a.data);
      setUsers((u.data?.users || []).filter((x) => x.role !== "admin"));
    } catch {
      toast.error(`Failed to load ${title} settings`);
    }
  }, [feature, title]);
  useEffect(() => { load(); }, [load]);

  const save = async (patch, okMsg) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/feature-access/${feature}`, patch);
      setCfg(res.data);
      toast.success(okMsg);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update access");
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return null;
  const allowed = cfg.allowed_user_ids || [];
  const allowedUsers = users.filter((u) => allowed.includes(u.id));
  const candidates = users.filter((u) => !allowed.includes(u.id));

  return (
    <div data-testid={`${testPrefix}-access-panel`} className="space-y-4">
      <h3 className="font-mono font-bold text-sm">{title}</h3>
      <div className="rounded-lg border border-border/50 bg-card/50 p-4 space-y-4">
        <div className="flex items-start gap-3">
          <Icon className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="font-mono font-bold text-sm text-card-foreground">{question}</p>
            <p className="font-mono text-xs text-muted-foreground">Changes apply instantly — no redeploy needed.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {MODES(noun).map((m) => (
            <button
              key={m.id}
              onClick={() => save({ mode: m.id }, `${title}: ${m.label}`)}
              disabled={saving}
              className={`text-left rounded-lg border px-3 py-2 transition-colors ${cfg.mode === m.id ? "border-primary bg-primary/15" : "border-border/50 hover:border-primary/50"}`}
              data-testid={`${testPrefix}-mode-${m.id}`}
            >
              <span className={`block font-mono text-xs font-bold ${cfg.mode === m.id ? "text-primary" : "text-card-foreground"}`}>{m.label}</span>
              <span className="block font-mono text-[10px] text-muted-foreground">{m.hint}</span>
            </button>
          ))}
        </div>

        {cfg.mode === "selected_users" && (
          <div className="space-y-2 pt-3 border-t border-border/30">
            <Label className="font-mono text-xs text-muted-foreground">Granted users (admins always have access)</Label>
            <div className="flex items-center gap-2">
              <select
                value={pickId}
                onChange={(e) => setPickId(e.target.value)}
                className="h-8 px-2 rounded-md border border-border bg-background font-mono text-xs max-w-sm flex-1"
                data-testid={`${testPrefix}-add-user-select`}
              >
                <option value="">Choose a user…</option>
                {candidates.map((u) => (
                  <option key={u.id} value={u.id}>{u.name || "User"} · {u.email}</option>
                ))}
              </select>
              <Button
                size="sm"
                className="btn-3d h-8"
                disabled={!pickId || saving}
                onClick={() => { save({ allowed_user_ids: [...allowed, pickId] }, "User granted"); setPickId(""); }}
                data-testid={`${testPrefix}-add-user-btn`}
              >
                <UserPlus className="w-3.5 h-3.5 mr-1" /> Grant
              </Button>
            </div>
            {allowedUsers.length > 0 ? (
              <ul className="space-y-1 mt-1" data-testid={`${testPrefix}-allowed-list`}>
                {allowedUsers.map((u) => (
                  <li key={u.id} className="flex items-center justify-between gap-2 px-2 py-1 rounded bg-secondary/40 text-xs font-mono">
                    <span className="truncate"><strong>{u.name || "User"}</strong><span className="text-muted-foreground"> · {u.email}</span></span>
                    <Button
                      size="icon" variant="ghost"
                      className="h-6 w-6 text-rose-400 hover:text-rose-300"
                      disabled={saving}
                      onClick={() => save({ allowed_user_ids: allowed.filter((id) => id !== u.id) }, "Access revoked")}
                      title="Revoke"
                      data-testid={`${testPrefix}-allowed-revoke-${u.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="font-mono text-[10px] text-muted-foreground" data-testid={`${testPrefix}-allowed-empty`}>No users granted yet — only admins have access.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const FunTimeAccessPanel = () => (
  <AccessPanel
    feature="fun_time"
    title="Fun Time Access"
    question="Who can use the Fun Time tools?"
    noun="the Fun Time tab"
    icon={PartyPopper}
    testPrefix="funtime"
  />
);

export const LottoScanAccessPanel = () => (
  <AccessPanel
    feature="lotto_scan"
    title="Lotto Scanning Access"
    question="Who can use Lotto Scanning?"
    noun="the Lotto Scanning tab"
    icon={ScanLine}
    testPrefix="lottoscan"
  />
);
